import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '../sectepe-ui/index';
import { useAuth } from '../contexts/AuthContext';
import authService from '../services/auth.service';
import { getAccessToken } from '../services/tokenStore';
import { PageHeader, LoadingState, StatusBadge } from '../components/ui';
import { Icon } from '../components/icons';
import { fehlerText, zeit } from '../vertrieb/format';
import { appRolleLabel, systemRolle, initialen } from '../verwaltung/rollen';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';
import { runtimeEnv } from '../types/runtimeEnv';

/**
 * Kontoseite von Keycloak (Passwort, Sitzungen, zweiter Faktor):
 * <OIDC_ISSUER_URL>/account. Null, solange env.js keinen Issuer nennt.
 */
export function keycloakKontoUrl(): string | null {
  const issuer = runtimeEnv().OIDC_ISSUER_URL.replace(/\/$/, '');
  return issuer ? `${issuer}/account` : null;
}

/**
 * Profil: Rolle auf Deutsch mit Hinweis, Anmeldedaten, Passwort. Im
 * Keycloak-Betrieb (ssoEnabled) gibt es kein lokales Passwort -- der
 * Knopf fehlt, stattdessen fuehrt ein Link zur Keycloak-Kontoseite
 * (Verbesserungsplan, Paket 2 Schritt 5).
 */
const ProfilePage: React.FC = () => {
  const { user, logout } = useAuth();
  const { darf, ssoEnabled } = useBerechtigung();
  const navigate = useNavigate();
  const { push } = useToast();
  const [zeigePasswort, setZeigePasswort] = useState(false);
  const [alt, setAlt] = useState('');
  const [neu, setNeu] = useState('');
  const [wiederholt, setWiederholt] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  if (!user) return <div className="page-container verwaltung-seite"><LoadingState rows={2} label="Profil wird geladen" /></div>;

  const abmelden = async () => {
    await logout();
    navigate('/login');
  };

  const formularZu = () => {
    setZeigePasswort(false);
    setFehler(null);
    setAlt('');
    setNeu('');
    setWiederholt('');
  };

  const passwortAendern = async (e: React.FormEvent) => {
    e.preventDefault();
    if (neu.length < 8) { setFehler('Das neue Passwort braucht mindestens 8 Zeichen.'); return; }
    if (neu !== wiederholt) { setFehler('Die beiden neuen Passwörter stimmen nicht überein.'); return; }
    if (neu === alt) { setFehler('Das neue Passwort muss sich vom alten unterscheiden.'); return; }
    setSpeichert(true);
    setFehler(null);
    try {
      const token = getAccessToken();
      if (!token) throw new Error('Nicht angemeldet');
      await authService.changePassword(token, alt, neu);
      formularZu();
      push({ message: 'Passwort geändert. Bitte gleich neu anmelden.', severity: 'success' });
      window.setTimeout(() => { logout(); navigate('/login'); }, 2000);
    } catch (err) {
      setFehler(fehlerText(err, 'Passwort konnte nicht geändert werden'));
    } finally {
      setSpeichert(false);
    }
  };

  const rolle = appRolleLabel(user);
  const kontoUrl = ssoEnabled ? keycloakKontoUrl() : null;
  const darfVerwalten = darf('admin.mandant') || darf('admin.benutzer');

  return (
    <div className="page-container verwaltung-seite">
      <PageHeader eyebrow="Konto" title="Profil" />

      <section className="card profil-kopf" aria-label="Konto">
        <span className="benutzer-avatar benutzer-avatar--gross" aria-hidden="true">{initialen(user.name)}</span>
        <div>
          <h2 className="card-title">{user.name}</h2>
          <div className="text-secondary">{user.email}</div>
          <div className="profil-kopf__badges">
            <StatusBadge tone={rolle.tone} title={rolle.hinweis}>{rolle.label}</StatusBadge>
            {/* Plattform-Admin (alle Mandanten) ist mehr als die App-Rolle; "Benutzer" waere nur Rauschen. */}
            {user.role === 'admin' && <StatusBadge tone="neutral">{systemRolle(user.role)}</StatusBadge>}
          </div>
        </div>
      </section>

      <section className="card" aria-label="Anmeldung">
        <dl className="sales-facts">
          <div><dt>Darf</dt><dd>{rolle.hinweis}</dd></div>
          {ssoEnabled && <div><dt>Anmeldung</dt><dd>Über Keycloak (SSO); Rollen werden dort vergeben.</dd></div>}
          {user.lastLogin && <div><dt>Letzte Anmeldung</dt><dd>{zeit(user.lastLogin)}</dd></div>}
          {user.createdAt && <div><dt>Konto seit</dt><dd>{zeit(user.createdAt)}</dd></div>}
        </dl>
      </section>

      <section className="card" aria-label="Passwort">
        <h2 className="card-title">Passwort</h2>
        {ssoEnabled ? (
          <>
            <p className="text-sm text-secondary">Passwort und Anmeldung werden in Keycloak verwaltet.</p>
            {kontoUrl && (
              <a href={kontoUrl} target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                <Icon name="external-link" size={14} /> Konto in Keycloak öffnen
              </a>
            )}
          </>
        ) : !zeigePasswort ? (
          <>
            <p className="text-sm text-secondary">Nach dem Ändern werden Sie abgemeldet und melden sich mit dem neuen Passwort an.</p>
            <button type="button" className="btn btn-secondary" onClick={() => setZeigePasswort(true)}><Icon name="lock" size={14} /> Passwort ändern</button>
          </>
        ) : (
          <form className="inhalt-formular" onSubmit={passwortAendern} aria-label="Neues Passwort">
            {fehler && <div className="alert alert-error">{fehler}</div>}
            <div className="inhalt-felder">
              <label>Aktuelles Passwort<input type="password" autoComplete="current-password" required value={alt} onChange={(e) => setAlt(e.target.value)} disabled={speichert} autoFocus /></label>
              <label>Neues Passwort (mind. 8 Zeichen)<input type="password" autoComplete="new-password" required minLength={8} value={neu} onChange={(e) => setNeu(e.target.value)} disabled={speichert} /></label>
              <label>Neues Passwort wiederholen<input type="password" autoComplete="new-password" required value={wiederholt} onChange={(e) => setWiederholt(e.target.value)} disabled={speichert} /></label>
            </div>
            <div className="btn-group">
              <button type="submit" className="btn btn-primary" disabled={speichert}>{speichert ? 'Speichert…' : 'Speichern'}</button>
              <button type="button" className="btn btn-ghost" onClick={formularZu} disabled={speichert}>Abbrechen</button>
            </div>
          </form>
        )}
      </section>

      <section className="card profil-fuss" aria-label="Abmelden">
        <span className="text-sm text-secondary">
          {darfVerwalten ? (
            <>
              Verwalten:{' '}
              {darf('admin.mandant') && <Link to="/tenant-management">Mandant</Link>}
              {darf('admin.mandant') && darf('admin.benutzer') && ' · '}
              {darf('admin.benutzer') && <Link to="/user-management">Benutzer</Link>}
            </>
          ) : (
            `Fragen zu Rollen und Zugängen beantwortet die Inhaber:in deines Mandanten${ssoEnabled ? '; Rollen werden in Keycloak vergeben' : ''}.`
          )}
        </span>
        <button type="button" className="btn btn-secondary" onClick={abmelden}><Icon name="logout" size={14} /> Abmelden</button>
      </section>
    </div>
  );
};

export default ProfilePage;
